import { SyntheticEvent, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Alert, Button, Group, Image, Modal, SimpleGrid, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { getPodcastImage, ImageSizes } from "@/core/helpers";
import { useStores } from "@/hooks/useStores";
import type { ProfileError } from "@/stores/AuthStore";
import styles from "./ProfileSetupModal.module.css";

const SUGGESTIONS = 24;

const messages: Record<ProfileError, string> = {
    invalid_name: "Namnet måste vara mellan 1 och 60 tecken.",
    invalid_podcasts: "Vi kunde inte spara dina poddar. Försök igen.",
    network: "Något gick fel. Kontrollera anslutningen och försök igen.",
};

/** Shown once after a new account's first sign-in. Everything is optional. */
export const ProfileSetupModal = observer(() => {
    const { authStore, toplistStore } = useStores();
    const [name, setName] = useState("");
    const [selected, setSelected] = useState<number[]>([]);
    const opened = authStore.needsProfile;

    useEffect(() => {
        if (!opened) return;
        setName(authStore.user?.name ?? "");
        setSelected([]);
        if (toplistStore.toplist.length === 0) {
            toplistStore.loadToplist().catch((error) => console.error("Failed to load suggestions:", error));
        }
    }, [opened, authStore, toplistStore]);

    const podcasts = toplistStore.toplist
        .map((entry) => entry.podcast)
        .filter((podcast): podcast is Podcast => podcast !== undefined)
        .slice(0, SUGGESTIONS);

    const toggle = (id: number) =>
        setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

    const save = async (event?: SyntheticEvent) => {
        event?.preventDefault();
        await authStore.saveProfile({
            name: name.trim() === "" ? undefined : name.trim(),
            podcastIds: selected.length > 0 ? selected : undefined,
        });
    };

    return (
        <Modal centered onClose={() => authStore.skipProfile()} opened={opened} size="lg" title="Välkommen!">
            <form onSubmit={save}>
                <Stack gap="md">
                    <Text size="sm">
                        Berätta lite om dig så kan vi anpassa HittaPodd efter dig. Allt är frivilligt och du kan hoppa
                        över det.
                    </Text>
                    <TextInput
                        autoComplete="nickname"
                        label="Vad ska vi kalla dig?"
                        maxLength={60}
                        onChange={(event) => setName(event.currentTarget.value)}
                        value={name}
                    />
                    {podcasts.length > 0 && (
                        <Stack gap="xs">
                            <Text fw={500} size="sm">
                                Vilka poddar gillar du?
                            </Text>
                            <SimpleGrid cols={{ base: 3, sm: 4 }} spacing="xs">
                                {podcasts.map((podcast) => (
                                    <UnstyledButton
                                        aria-pressed={selected.includes(podcast.id)}
                                        className={styles.choice}
                                        key={podcast.id}
                                        onClick={() => toggle(podcast.id)}
                                        title={podcast.name}
                                        type="button">
                                        <Image
                                            alt={podcast.name}
                                            h="auto"
                                            radius="md"
                                            src={getPodcastImage(podcast, ImageSizes.Small)}
                                            w="100%"
                                        />
                                    </UnstyledButton>
                                ))}
                            </SimpleGrid>
                            <Text c="dimmed" size="xs">
                                {selected.length === 0 ? "Välj så många du vill." : `${selected.length} valda`}
                            </Text>
                        </Stack>
                    )}
                    {authStore.profileError && (
                        <Alert color="red" role="alert">
                            {messages[authStore.profileError]}
                        </Alert>
                    )}
                    <Group justify="space-between">
                        <Button disabled={authStore.busy} onClick={() => authStore.skipProfile()} variant="subtle">
                            Hoppa över
                        </Button>
                        <Button loading={authStore.busy} type="submit">
                            Spara
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
});
